<template>
  <div class="qr-scan-overlay">
    <div class="qr-scan-container" @click.stop>
      <!-- 헤더 -->
      <div class="qr-scan-header">
        <span class="header-title">{{ $t('QR 코드 스캔') }}</span>
        <feather-icon
          class="close-icon"
          icon="XIcon"
          @click="closeDialog"
        />
      </div>

      <!-- 카메라 영역 -->
      <div class="camera-container">
        <video
          ref="video"
          class="camera-video"
          autoplay
          playsinline
          muted
        ></video>

        <!-- 카메라 제어 버튼들 -->
        <div class="camera-controls">
          <!-- 카메라 전환 버튼 -->
          <button
            v-if="frontCameras.length > 0 && backCameras.length > 0"
            class="control-button camera-switch-btn"
            @click="switchCamera"
            :disabled="isLoading || isSwitchingCamera"
          >
            <feather-icon icon="RotateCcwIcon" size="20" />
          </button>

          <!-- 플래시 버튼 -->
          <button
            v-if="hasFlash"
            class="control-button flash-btn"
            :class="{ active: flashEnabled }"
            @click="toggleFlash"
            :disabled="isLoading || isSwitchingCamera"
          >
            <feather-icon :icon="flashEnabled ? 'ZapIcon' : 'ZapOffIcon'" size="20" />
          </button>
        </div>

        <!-- 스캔 영역 표시 -->
        <div class="scan-region">
          <div class="scan-frame">
            <!-- 모서리 표시 -->
            <div class="corner corner-top-left"></div>
            <div class="corner corner-top-right"></div>
            <div class="corner corner-bottom-left"></div>
            <div class="corner corner-bottom-right"></div>
          </div>
        </div>

        <!-- 로딩 표시 -->
        <div v-if="isLoading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span class="loading-text">{{ $t('카메라를 준비 중입니다...') }}</span>
        </div>

        <!-- 에러 메시지 -->
        <div v-if="errorMessage" class="error-overlay">
          <feather-icon icon="AlertCircleIcon" size="48" />
          <span class="error-text">{{ errorMessage }}</span>
          <div class="error-actions">
            <vs-button
              class="retry-button"
              color="primary"
              type="filled"
              @click="retryWithPermissionCheck"
            >{{ $t('다시 시도') }}</vs-button>
            <vs-button
              v-if="errorMessage.includes('카메라 권한')"
              class="help-button"
              color="dark"
              type="border"
              @click="openBrowserSettings"
            >{{ $t('설정 도움말') }}</vs-button>
          </div>
        </div>
      </div>

      <!-- 안내 텍스트 -->
      <div class="instruction-text" :class="{ 'mobile-instruction': isMobile }">{{ $t('QR 코드를 스캔해주세요.') }}</div>

      <!-- 하단 버튼 (데스크톱에서만 표시) -->
      <div v-if="!isMobile" class="bottom-actions">
        <vs-button
          class="cancel-button"
          color="dark"
          type="border"
          @click="closeDialog"
        >{{ $t('취소') }}</vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import * as UiUtil from "@/util/UiUtil";
import QrScanner from "qr-scanner";

export default {
  name: 'QrScanCamera',
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      qrScanner: null,
      stream: null,
      availableCameras: [],
      currentCameraIndex: 0,
      frontCameras: [],
      backCameras: [],
      currentFacingMode: 'environment',
      hasFlash: false,
      flashEnabled: false,
      isMobile: false,
      isIOS: false,
      isSwitchingCamera: false,
      autoScanTimer: null,
      historyStateAdded: false
    };
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    },

    // 모바일에서 viewport 변경 처리 (주소창 표시/숨김 등)
    handleViewportChange() {
      if (!this.isMobile) return;

      // viewport 변경 시 레이아웃 재계산을 위해 약간의 지연 후 처리
      setTimeout(() => {
        this.checkMobile();
        // 필요시 카메라 영역 재조정
        if (this.$refs.video) {
          this.$refs.video.style.height = 'auto';
          this.$nextTick(() => {
            this.$refs.video.style.height = '100%';
          });
        }
      }, 100);
    },

    async getCameras() {
      try {
        // 먼저 권한 요청을 위해 일시적으로 스트림 생성
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });

        // 카메라 목록 가져오기
        this.availableCameras = await QrScanner.listCameras(true);

        // 일시적 스트림 정리
        tempStream.getTracks().forEach(track => track.stop());

        // console.log('Available cameras:', this.availableCameras);

        // 카메라를 전면/후면으로 분류
        this.frontCameras = [];
        this.backCameras = [];

        for (const camera of this.availableCameras) {
          const label = camera.label.toLowerCase();

          // 전면 카메라 감지 키워드
          const frontKeywords = ['front', 'user', 'selfie', '전면', 'face'];
          // 후면 카메라 감지 키워드
          const backKeywords = ['back', 'rear', 'environment', '후면', '뒤', 'main', 'wide', 'ultra', 'tele'];

          const isFront = frontKeywords.some(keyword => label.includes(keyword));
          const isBack = backKeywords.some(keyword => label.includes(keyword));

          if (isFront && !isBack) {
            this.frontCameras.push(camera);
          } else if (isBack || (!isFront && !isBack)) {
            // 명시적으로 후면이거나, 구분이 안 되는 경우 후면으로 분류
            this.backCameras.push(camera);
          }
        }

        // console.log('Front cameras:', this.frontCameras);
        // console.log('Back cameras:', this.backCameras);

        // 저장된 카메라 방향 설정 불러오기
        const savedFacingMode = this.getCameraSetting();
        // console.log('Saved camera setting:', savedFacingMode);

        // 저장된 설정이 유효한지 확인 후 적용
        if (savedFacingMode === 'user' && this.frontCameras.length > 0) {
          this.currentFacingMode = 'user';
        } else if (savedFacingMode === 'environment' && this.backCameras.length > 0) {
          this.currentFacingMode = 'environment';
        } else {
          // 저장된 설정이 유효하지 않은 경우 기본값 적용
          this.currentFacingMode = this.backCameras.length > 0 ? 'environment' : 'user';
        }

        // console.log('Applied camera facing mode:', this.currentFacingMode);

      } catch (error) {
        console.error('Error getting cameras:', error);
        // 권한이 없는 경우에도 기본 카메라 목록 시도
        try {
          this.availableCameras = await QrScanner.listCameras(false);
          // 권한 없이도 기본 분류 시도
          this.frontCameras = [];
          this.backCameras = this.availableCameras.length > 0 ? [this.availableCameras[0]] : [];
        } catch (fallbackError) {
          console.error('Fallback camera list error:', fallbackError);
          // 카메라 목록을 가져올 수 없어도 빈 배열로 초기화하여 다이얼로그는 열리도록 함
          this.availableCameras = [];
          this.frontCameras = [];
          this.backCameras = [];
        }
      }
    },

    async openDialog() {
      this.errorMessage = '';

      // 카메라 목록이 없는 경우에만 가져오기
      if (this.availableCameras.length === 0) {
        await this.getCameras();
      }

      await this.$nextTick();
      await this.initCamera();
    },

    closeDialog() {
      this.cleanup();
      this.$emit('back');
    },

    async initCamera() {
      this.isLoading = true;
      // 카메라 전환 중이 아닐 때만 에러 메시지 초기화
      if (!this.isSwitchingCamera) {
        this.errorMessage = '';
      }

      try {
        // 기존 리소스 완전 정리
        await this.cleanupCameraResources();

        // iOS에서 더 긴 대기 시간
        const waitTime = this.isIOS ? 400 : 200;
        // console.log(`Waiting ${waitTime}ms for resource cleanup...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));

        // 현재 facing mode에 따라 카메라 선택
        const targetCameras = this.currentFacingMode === 'user' ? this.frontCameras : this.backCameras;
        const selectedCamera = targetCameras.length > 0 ? targetCameras[0] : null;

        // console.log('Target facing mode:', this.currentFacingMode);
        // console.log('Available target cameras:', targetCameras.length);
        // console.log('Selected camera:', selectedCamera);

        const constraints = {
          video: {
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            facingMode: this.currentFacingMode,
            focusMode: { ideal: 'continuous' },
            exposureMode: { ideal: 'continuous' },
            whiteBalanceMode: { ideal: 'continuous' }
          }
        };

        // iOS에서는 deviceId 사용을 더 신중하게 처리
        if (selectedCamera && selectedCamera.id && !this.isIOS) {
          // 안드로이드에서만 deviceId 사용
          constraints.video.deviceId = { ideal: selectedCamera.id };
        } else if (this.isIOS && selectedCamera && selectedCamera.id) {
          // iOS에서도 deviceId를 ideal로 시도해보되, 실패 시 facingMode로 폴백
          constraints.video.deviceId = { ideal: selectedCamera.id };
          // console.log('iOS: Trying deviceId with fallback to facingMode');
        } else if (this.isIOS) {
          // iOS에서 deviceId가 없는 경우에만 facingMode만 사용
          // console.log('iOS: Using facingMode only for better compatibility');
        }

        // console.log('Camera constraints:', constraints);

        // 카메라 권한 요청 및 스트림 생성
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        // console.log('Camera stream obtained');

        // 비디오 엘리먼트에 스트림 연결
        const video = this.$refs.video;
        if (video) {
          video.srcObject = this.stream;

          // 비디오 로드 대기 (iOS에서 더 긴 타임아웃)
          const loadTimeout = this.isIOS ? 12000 : 8000;
          // console.log(`Waiting for video load (timeout: ${loadTimeout}ms)...`);

          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error(`Video load timeout after ${loadTimeout}ms`));
            }, loadTimeout);

            video.onloadedmetadata = () => {
              clearTimeout(timeout);
              // console.log('Video metadata loaded');
              resolve();
            };
            video.onerror = (event) => {
              clearTimeout(timeout);
              console.error('Video load error:', event);
              reject(new Error('Video load error'));
            };
          });

          // 비디오 재생 시작
          try {
            await video.play();
            // console.log('Video playback started');
          } catch (playError) {
            console.warn('Video play warning:', playError);
            // iOS에서 autoplay 정책 때문에 실패할 수 있지만 계속 진행
          }
        }

        // 플래시 지원 확인
        const track = this.stream.getVideoTracks()[0];
        if (track) {
          const capabilities = track.getCapabilities();
          this.hasFlash = capabilities && capabilities.torch === true;
          // console.log('Flash support:', this.hasFlash);
        }

        // QR 스캐너 초기화 - 카메라만 사용하고 자동 스캔은 비활성화
        this.qrScanner = new QrScanner(
          video,
          () => {}, // 더미 콜백 (사용하지 않음)
          {
            highlightScanRegion: false,
            highlightCodeOutline: false,
            preferredCamera: selectedCamera ? selectedCamera.id : this.currentFacingMode
          }
        );

        // 스캐너를 시작하지 않고 카메라만 사용
        // await this.qrScanner.start();
        this.isLoading = false;

        // console.log('Camera initialized successfully. Facing mode:', this.currentFacingMode);
        // console.log('QrScanner started. Active:', this.qrScanner._active);
        // console.log('QrScanner hasFlash:', this.qrScanner.hasFlash());

        // 자체적인 자동 스캔 시작
        this.startAutoScan();

      } catch (error) {
        console.error('Camera initialization error:', error);
        this.isLoading = false;

        // 카메라 전환 중일 때는 에러 메시지를 표시하지 않음
        if (!this.isSwitchingCamera) {
          if (error.name === 'NotAllowedError') {
            this.errorMessage = this.getCameraPermissionGuide();
          } else if (error.name === 'NotFoundError') {
            this.errorMessage = '카메라를 찾을 수 없습니다.';
          } else if (error.name === 'NotSupportedError') {
            this.errorMessage = '이 브라우저에서는 카메라를 지원하지 않습니다.';
          } else {
            this.errorMessage = '카메라를 초기화하는 중 오류가 발생했습니다.';
          }
        } else {
          // 카메라 전환 실패 시 에러를 throw하여 switchCamera에서 처리하도록 함
          throw error;
        }
      }
    },

    async cleanupCameraResources() {
      // console.log('Starting camera cleanup...');

      // 플래시 끄기
      if (this.flashEnabled && this.stream) {
        try {
          const track = this.stream.getVideoTracks()[0];
          await track.applyConstraints({
            advanced: [{ torch: false }]
          });
          this.flashEnabled = false;
          // console.log('Flash disabled');
        } catch (error) {
          console.error('Flash cleanup error:', error);
        }
      }

      // QR 스캐너 정리
      if (this.qrScanner) {
        try {
          this.qrScanner.destroy();
          // console.log('QR scanner destroyed');
        } catch (error) {
          console.error('QR scanner cleanup error:', error);
        }
        this.qrScanner = null;
      }

      // 미디어 스트림 정리 (iOS에서 더 신중하게)
      if (this.stream) {
        this.stream.getTracks().forEach((track, index) => {
          // console.log(`Stopping track ${index}:`, track.kind);
          track.stop();
        });
        this.stream = null;
        // console.log('Media stream stopped');
      }

      // 비디오 엘리먼트 정리
      if (this.$refs.video) {
        this.$refs.video.srcObject = null;
        // iOS에서 추가 정리
        if (this.isIOS) {
          this.$refs.video.load();
        }
        // console.log('Video element cleaned');
      }

      // iOS에서 추가 대기 시간
      if (this.isIOS) {
        // console.log('Waiting extra time for iOS cleanup...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // console.log('Camera cleanup completed');
    },

    async switchCamera() {
      // 카메라가 1개 이하이거나 이미 전환 중인 경우 리턴
      if ((this.frontCameras.length + this.backCameras.length) <= 1 || this.isSwitchingCamera) {
        return;
      }

      // console.log('Starting camera switch from:', this.currentFacingMode);
      // console.log('iOS device:', this.isIOS);

      this.isSwitchingCamera = true;
      const previousFacingMode = this.currentFacingMode;

      try {
        // 현재 방향의 반대로 전환
        if (this.currentFacingMode === 'environment') {
          // 후면 -> 전면으로 전환 (전면 카메라가 있는 경우만)
          if (this.frontCameras.length > 0) {
            this.currentFacingMode = 'user';
          } else {
            throw new Error('전면 카메라를 사용할 수 없습니다.');
          }
        } else if (this.backCameras.length > 0) {
          // 전면 -> 후면으로 전환 (후면 카메라가 있는 경우만)
          this.currentFacingMode = 'environment';
        } else {
          throw new Error('후면 카메라를 사용할 수 없습니다.');
        }

        // console.log('Switching to facing mode:', this.currentFacingMode);

        // iOS에서는 더 긴 대기 시간과 추가 처리
        if (this.isIOS) {
          // console.log('iOS: Applying additional cleanup...');
          // 기존 리소스 완전 정리
          await this.cleanupCameraResources();
          // iOS에서 더 긴 대기
          await new Promise(resolve => setTimeout(resolve, 800));
        }

        // 카메라 재초기화
        await this.initCamera();
        // console.log('Camera switch successful');

        // 카메라 전환 성공 시 설정 저장
        this.saveCameraSetting();

      } catch (error) {
        console.error('Camera switch error:', error);

        // 카메라 전환 실패 시 이전 설정으로 되돌리기
        this.currentFacingMode = previousFacingMode;

        try {
          await this.initCamera();
          // console.log('Reverted to previous camera mode:', previousFacingMode);
        } catch (revertError) {
          console.error('Failed to revert camera:', revertError);
          this.errorMessage = '카메라 전환 중 오류가 발생했습니다.';
        }
      } finally {
        this.isSwitchingCamera = false;
      }
    },

    async toggleFlash() {
      if (!this.hasFlash || !this.stream) return;

      try {
        const track = this.stream.getVideoTracks()[0];
        await track.applyConstraints({
          advanced: [
            {
              torch: !this.flashEnabled
            }
          ]
        });
        this.flashEnabled = !this.flashEnabled;
      } catch (error) {
        console.error('Flash toggle error:', error);
      }
    },

    playBeepSound() {
      try {
        // Web Audio API를 사용한 beep 사운드 생성
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (error) {
        console.error('Beep sound error:', error);
      }
    },

    onScanSuccess(result) {
      // 자동 스캔 중단
      this.stopAutoScan();

      // beep 효과음 재생
      this.playBeepSound();

      // QR 코드 텍스트 데이터 추출
      let qrData = '';
      if (typeof result === 'string') {
        qrData = result;
      } else if (result && typeof result === 'object') {
        // QrScanner 라이브러리에서 반환하는 객체에서 텍스트 데이터 추출
        qrData = result.data || result.text || result.content || JSON.stringify(result);
      } else {
        qrData = String(result);
      }

      // QR 코드 스캔 결과 표시
      this.$emit("success", qrData)

      // 스캔 완료 후 다이얼로그 닫기 --> QrScanManager 에서 처리
      // this.closeDialog();
    },

    cleanup() {
      // 스캔 모니터링 타이머 정리
      if (this.scanStatusTimer) {
        clearTimeout(this.scanStatusTimer);
        this.scanStatusTimer = null;
      }

      // 자동 스캔 중단
      this.stopAutoScan();

      // 카메라 리소스 정리
      this.cleanupCameraResources();

      // 상태 초기화
      this.hasFlash = false;
      this.isSwitchingCamera = false;
      this.scanStartTime = null;
      this.scanAttempts = 0;
      this.lastScanAttempt = 0;
    },

    getCameraPermissionGuide() {
      const userAgent = navigator.userAgent.toLowerCase();
      let guide = '카메라 권한이 필요합니다.\n\n';

      if (userAgent.includes('chrome')) {
        guide += '🔧 Chrome에서 권한 허용하기:\n';
        guide += '1. 주소창 왼쪽의 🔒 또는 🚫 아이콘 클릭\n';
        guide += '2. "카메라" 항목을 "허용"으로 변경\n';
        guide += '3. 페이지 새로고침';
      } else if (userAgent.includes('firefox')) {
        guide += '🔧 Firefox에서 권한 허용하기:\n';
        guide += '1. 주소창 왼쪽의 🛡️ 아이콘 클릭\n';
        guide += '2. "권한" > "카메라" 항목 허용\n';
        guide += '3. 페이지 새로고침';
      } else if (userAgent.includes('safari')) {
        guide += '🔧 Safari에서 권한 허용하기:\n';
        guide += '1. Safari > 환경설정 > 웹사이트\n';
        guide += '2. "카메라" 섹션에서 이 사이트 허용\n';
        guide += '3. 페이지 새로고침';
      } else {
        guide += '🔧 브라우저에서 권한 허용하기:\n';
        guide += '1. 브라우저 설정에서 카메라 권한 허용\n';
        guide += '2. 페이지 새로고침';
      }

      return guide;
    },

    async checkCameraPermission() {
      try {
        const result = await navigator.permissions.query({ name: 'camera' });
        return result.state;
      } catch (error) {
        console.error('Permission check error:', error);
        return 'unknown';
      }
    },

    async retryWithPermissionCheck() {
      const permissionState = await this.checkCameraPermission();

      if (permissionState === 'denied') {
        this.errorMessage = this.getCameraPermissionGuide();
        return;
      }

      // 권한이 허용되었거나 확인할 수 없는 경우 카메라 초기화 재시도
      await this.initCamera();
    },

    openBrowserSettings() {
      const userAgent = navigator.userAgent.toLowerCase();
      let helpUrl = '';

      if (userAgent.includes('chrome')) {
        helpUrl = 'chrome://settings/content/camera';
      } else if (userAgent.includes('firefox')) {
        helpUrl = 'about:preferences#privacy';
      } else {
        // 일반적인 도움말 페이지로 안내
        alert('브라우저 설정에서 이 사이트의 카메라 권한을 허용해주세요.\n\n설정 방법:\n1. 브라우저 설정 열기\n2. 개인정보 또는 권한 섹션 찾기\n3. 카메라 권한에서 이 사이트 허용\n4. 페이지 새로고침');
        return;
      }

      try {
        window.open(helpUrl, '_blank');
      } catch (error) {
        alert('브라우저 설정 페이지를 열 수 없습니다.\n수동으로 브라우저 설정에서 카메라 권한을 허용해주세요.');
      }
    },

    // localStorage에서 사용자별 카메라 설정 불러오기
    getCameraSetting() {
      try {
        const key = `qr_camera_setting_${this.userId}`;
        const saved = localStorage.getItem(key);
        return saved || 'environment'; // 기본값은 후면 카메라
      } catch (error) {
        console.error('Error loading camera setting:', error);
        return 'environment';
      }
    },

    // localStorage에 사용자별 카메라 설정 저장
    saveCameraSetting() {
      try {
        const key = `qr_camera_setting_${this.userId}`;
        localStorage.setItem(key, this.currentFacingMode);
        // console.log('Camera setting saved:', this.currentFacingMode);
      } catch (error) {
        console.error('Error saving camera setting:', error);
      }
    },

    startAutoScan() {
      const performAutoScan = async () => {
        if (!this.$refs.video) {
          return;
        }

        try {
          // 기존 방식: 전체 비디오 영역을 스캔
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const video = this.$refs.video;

          // 전체 비디오 크기로 canvas 설정
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // 중앙 영역 크롭을 위한 계산 (개발 완료 후 주석 해제하여 사용)
          const cropSize = Math.min(canvas.width, canvas.height) * 0.6; // 화면의 60% 크기
          const cropX = (canvas.width - cropSize) / 2;
          const cropY = (canvas.height - cropSize) / 2;

          // 크롭된 영역을 위한 새로운 canvas 생성
          const croppedCanvas = document.createElement('canvas');
          const croppedCtx = croppedCanvas.getContext('2d');
          croppedCanvas.width = cropSize;
          croppedCanvas.height = cropSize;

          // 중앙 영역만 크롭하여 새 canvas에 그리기
          croppedCtx.drawImage(
            canvas,
            cropX, cropY, cropSize, cropSize, // 소스 영역 (크롭할 부분)
            0, 0, cropSize, cropSize // 대상 영역 (새 canvas 전체)
          );

          // 크롭된 영역에서 QR 코드 스캔
          const result = await QrScanner.scanImage(croppedCanvas);

          if (result) {
            this.onScanSuccess(result);
            return; // 스캔 성공 시 자동 스캔 중단
          }
        } catch (error) {
          // 스캔 실패는 정상적인 상황이므로 조용히 처리
        }

        // 다음 자동 스캔 예약 (500ms 간격)
        this.autoScanTimer = setTimeout(performAutoScan, 500);
      };

      // 첫 번째 자동 스캔 시작
      this.autoScanTimer = setTimeout(performAutoScan, 1000);
    },

    stopAutoScan() {
      if (this.autoScanTimer) {
        clearTimeout(this.autoScanTimer);
        this.autoScanTimer = null;
      }
    }
  },
  mounted() {
    this.checkMobile();
    // getCameras(); // 제거: 다이얼로그가 열릴 때 호출하도록 변경

    // QR Scanner가 지원되는지 확인
    if (!QrScanner.hasCamera()) {
      this.errorMessage = '카메라를 사용할 수 없습니다.';
    }

    // 화면 크기 변경 감지
    window.addEventListener('resize', this.checkMobile);

    // 모바일에서 viewport 변경 감지 (주소창 숨김/표시 등)
    if (this.isMobile) {
      window.addEventListener('resize', this.handleViewportChange);
      // iOS Safari의 경우 orientationchange 이벤트도 감지
      window.addEventListener('orientationchange', this.handleViewportChange);
    }
  },
  beforeDestroy() {
    this.cleanup();
    window.removeEventListener('resize', this.checkMobile);

    if (this.isMobile) {
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('orientationchange', this.handleViewportChange);
    }
  },
}
</script>

<style lang="scss" scoped>
.qr-scan-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.qr-scan-container {
  width: 100%;
  max-width: 400px;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 600px;
  position: relative;
  opacity: 1 !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.qr-scan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: rgba(var(--vs-primary), 1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff !important;
  position: relative;
  z-index: 100;
  opacity: 1 !important;
  backdrop-filter: none !important;
}

.header-title {
  color: #fff !important;
  font-size: 18px;
  font-weight: 600;
  opacity: 1 !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  filter: none !important;
  backdrop-filter: none !important;
}

.close-icon {
  color: #fff !important;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
  opacity: 1 !important;
  filter: none !important;
  backdrop-filter: none !important;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
    transform: scale(1.05);
  }

  svg {
    color: #fff !important;
    fill: #fff !important;
    opacity: 1 !important;
  }
}

.camera-container {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  min-height: 400px;
}

.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-controls {
  position: absolute;
  top: 4.5rem;
  left: 1rem;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.7);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: rgba(74, 144, 226, 0.8);
    color: #fff;
  }
}

.scan-region {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  pointer-events: none;
  z-index: 5;
}

.scan-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(74, 144, 226, 0.8);
  border-radius: 8px;
  box-shadow:
    0 0 0 2000px rgba(0, 0, 0, 0.5),
    0 0 20px rgba(74, 144, 226, 0.5);
}

.corner {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid #4a90e2;
  border-radius: 4px;

  &.corner-top-left {
    top: -15px;
    left: -15px;
    border-right: none;
    border-bottom: none;
  }

  &.corner-top-right {
    top: -15px;
    right: -15px;
    border-left: none;
    border-bottom: none;
  }

  &.corner-bottom-left {
    bottom: -15px;
    left: -15px;
    border-right: none;
    border-top: none;
  }

  &.corner-bottom-right {
    bottom: -15px;
    right: -15px;
    border-left: none;
    border-top: none;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #333;
  border-top: 3px solid #4a90e2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.loading-text {
  font-size: 16px;
  text-align: center;
}

.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding: 2rem;
  text-align: center;
  z-index: 10;
}

.error-text {
  font-size: 14px;
  margin: 1rem 0;
  white-space: pre-line;
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;
}

.error-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
}

.retry-button,
.help-button {
  min-width: 120px;
}

.instruction-text {
  color: #fff;
  text-align: center;
  font-size: 16px;
  padding: 1.5rem;
  line-height: 1.5;
  background: #1a1a1a;

  &.mobile-instruction {
    position: relative;
    bottom: auto;
    left: auto;
    right: auto;
    background: rgba(26, 26, 26, 0.9);
    backdrop-filter: blur(4px);
    margin-top: auto;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0));
  }
}

.bottom-actions {
  padding: 1rem 1.5rem;
  background: #1a1a1a;
  border-top: 1px solid #333;
}

.cancel-button {
  width: 100%;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 모바일 최적화
@media (max-width: 768px) {
  .qr-scan-overlay {
    padding: 0;
    // 모바일에서 viewport 전체 사용하되 safe area 고려
    height: 100vh;
    height: 100dvh; // 동적 viewport height 사용 (지원하는 브라우저에서)
  }

  .qr-scan-container {
    margin: 0;
    border-radius: 0;
    height: 100vh;
    height: 100dvh; // 동적 viewport height 사용
    max-width: none;
    min-height: 100vh;
    min-height: 100dvh;
    // 상단과 하단 safe area 확보
    padding-top: env(safe-area-inset-top, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
    display: flex;
    flex-direction: column;
  }

  .camera-container {
    // 헤더와 안내텍스트를 제외한 나머지 공간 사용
    flex: 1;
    min-height: 0; // flex child가 내용에 맞춰 축소되도록 함
  }

  .scan-region {
    width: 200px;
    height: 200px;
  }

  .scan-frame {
    width: 100%;
    height: 100%;
  }

  .camera-controls {
    top: calc(6rem + env(safe-area-inset-top, 0));
  }

  .instruction-text.mobile-instruction {
    position: relative;
    bottom: auto;
    left: auto;
    right: auto;
    background: rgba(26, 26, 26, 0.9);
    backdrop-filter: blur(4px);
    margin-top: auto;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0));
  }
}

// Vue deep 선택자로 전역 스타일 오버라이드 방지
::v-deep .qr-scan-header {
  * {
    color: #fff !important;
    opacity: 1 !important;
  }
}

::v-deep .header-title {
  color: #fff !important;
  opacity: 1 !important;
}

::v-deep .close-icon {
  color: #fff !important;
  opacity: 1 !important;

  svg {
    color: #fff !important;
    fill: #fff !important;
    opacity: 1 !important;
  }
}
</style>
